/**
 * Confirmation Panel Component
 * Shows appointment details for review before final confirmation
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useAppointmentBooking } from '@/hooks';
import { useWidgetStore } from '@/store/widgetStore';
import type { Provider, AppointmentSlot } from '@/types';

// Icons
const CalendarIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const InfoIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="ww-w-4 ww-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const UserIcon = () => (
  <svg className="ww-w-full ww-h-full ww-text-gray-400" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

interface ConfirmationPanelProps {
  provider: Provider;
  slot: AppointmentSlot;
  service: string;
  location: string;
  patientInfo: {
    name: string;
    phone: string;
    email: string;
    dateOfBirth: string;
    reasonForVisit: string;
    insuranceProvider: string;
    memberId: string;
    groupMember: string;
  };
  paymentMethodId?: string;
  onConfirm: () => void;
  onBack: () => void;
}

export const ConfirmationPanel = ({
  provider,
  slot,
  service,
  location,
  patientInfo,
  paymentMethodId,
  onConfirm,
  onBack,
}: ConfirmationPanelProps) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { practice } = useWidgetStore();
  const { booking, loading, error, bookAppointment } = useAppointmentBooking();

  // When booking succeeds, call onConfirm to navigate to success page
  useEffect(() => {
    if (booking && booking.success) {
      onConfirm();
    }
  }, [booking, onConfirm]);

  const handleConfirm = async () => {
    if (!agreedToTerms || !practice) return;

    // Split name into first and last name
    const nameParts = patientInfo.name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || nameParts[0]; // Use first name as last if only one name provided

    // Extract time from slot
    const startTime = format(new Date(slot.startTime), 'HH:mm');
    const endTime = format(new Date(slot.endTime), 'HH:mm');
    const requestedDate = format(new Date(slot.startTime), 'yyyy-MM-dd');

    try {
      await bookAppointment({
        practiceId: practice.id,
        serviceId: service,
        clinicianId: provider.id,
        locationId: location,
        requestedDate,
        startTime,
        endTime,
        appointmentType: 'individual',
        locationType: 'in_person', // Default to in-person, could be made dynamic
        paymentMethodId, // Optional payment method
        patientInfo: {
          email: patientInfo.email,
          phone: patientInfo.phone,
          firstName,
          lastName,
          dateOfBirth: patientInfo.dateOfBirth, // Already in YYYY-MM-DD format from date input
          sex: 'prefer_not_to_say', // Default value, could add to form
          notes: patientInfo.reasonForVisit,
        },
      });
    } catch (err) {
      console.error('Failed to book appointment:', err);
    }
  };

  const startTime = new Date(slot.startTime);
  const endTime = new Date(slot.endTime);

  return (
    <div className="ww-bg-white ww-border ww-border-gray-200 ww-rounded-lg ww-p-6">
      {/* Provider Info */}
      <div className="ww-flex ww-items-center ww-gap-3 ww-pb-4 ww-border-b ww-border-gray-100">
        <div className="ww-w-12 ww-h-12 ww-rounded-full ww-overflow-hidden ww-bg-gray-100 ww-flex-shrink-0">
          {provider.avatarUrl ? (
            <img
              src={provider.avatarUrl}
              alt={provider.name}
              className="ww-w-full ww-h-full ww-object-cover"
            />
          ) : (
            <div className="ww-w-full ww-h-full ww-flex ww-items-center ww-justify-center">
              <UserIcon />
            </div>
          )}
        </div>
        <div>
          <h3 className="ww-font-semibold ww-text-gray-900">{provider.name}</h3>
          <p className="ww-text-sm ww-text-gray-500">{provider.specialty}</p>
        </div>
      </div>

      {/* Appointment Details */}
      <div className="ww-py-4 ww-space-y-3 ww-border-b ww-border-gray-100">
        <div className="ww-flex ww-items-center ww-gap-3 ww-text-gray-700">
          <CalendarIcon />
          <span className="ww-text-sm">{format(startTime, 'EEEE, MMMM d')}</span>
        </div>
        <div className="ww-flex ww-items-center ww-gap-3 ww-text-gray-700">
          <ClockIcon />
          <span className="ww-text-sm">
            {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
          </span>
        </div>
        <div className="ww-flex ww-items-center ww-gap-3 ww-text-gray-700">
          <ClipboardIcon />
          <span className="ww-text-sm">{service || slot.appointmentType} ({slot.duration} minutes)</span>
        </div>
      </div>

      {/* Before Your Appointment */}
      <div className="ww-py-4 ww-border-b ww-border-gray-100">
        <div className="ww-flex ww-items-start ww-gap-2 ww-mb-3">
          <InfoIcon />
          <h4 className="ww-font-medium ww-text-gray-900">Before Your Appointment</h4>
        </div>
        <ul className="ww-ml-7 ww-space-y-1 ww-text-sm ww-text-gray-600">
          <li className="ww-flex ww-items-start ww-gap-2">
            <span className="ww-mt-1.5 ww-w-1 ww-h-1 ww-bg-gray-400 ww-rounded-full ww-flex-shrink-0" />
            Arrive 15 mins early
          </li>
          <li className="ww-flex ww-items-start ww-gap-2">
            <span className="ww-mt-1.5 ww-w-1 ww-h-1 ww-bg-gray-400 ww-rounded-full ww-flex-shrink-0" />
            Bring your ID and insurance card
          </li>
          <li className="ww-flex ww-items-start ww-gap-2">
            <span className="ww-mt-1.5 ww-w-1 ww-h-1 ww-bg-gray-400 ww-rounded-full ww-flex-shrink-0" />
            Confirmation mail will be sent
          </li>
        </ul>
      </div>

      {/* Cancellation Policy */}
      <div className="ww-py-4 ww-border-b ww-border-gray-100">
        <div className="ww-flex ww-items-start ww-gap-2 ww-mb-2">
          <InfoIcon />
          <h4 className="ww-font-medium ww-text-gray-900">Cancellation Policy</h4>
        </div>
        <p className="ww-ml-7 ww-text-sm ww-text-gray-600">
          Cancel 24 hours in advance or you will be charged
        </p>
      </div>

      {/* Terms Agreement */}
      <div className="ww-py-4">
        <label className="ww-flex ww-items-start ww-gap-3 ww-cursor-pointer">
          <div className="ww-relative ww-mt-0.5">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="ww-sr-only"
            />
            <div
              className={`ww-w-5 ww-h-5 ww-border-2 ww-rounded ww-flex ww-items-center ww-justify-center ww-transition-colors ${
                agreedToTerms
                  ? 'ww-bg-primary ww-border-primary'
                  : 'ww-border-gray-300'
              }`}
            >
              {agreedToTerms && (
                <CheckIcon />
              )}
            </div>
          </div>
          <span className="ww-text-sm ww-text-gray-700">
            I agree to the{' '}
            <a href="#" className="ww-text-primary ww-underline">
              Terms and Policy
            </a>
          </span>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="ww-mb-4 ww-p-3 ww-bg-red-50 ww-border ww-border-red-200 ww-rounded-lg">
          <p className="ww-text-sm ww-text-red-600">{error}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="ww-flex ww-gap-3">
        <button
          onClick={onBack}
          disabled={loading}
          className="ww-flex-1 ww-py-3 ww-rounded-lg ww-font-medium ww-text-sm ww-transition-colors ww-border ww-border-gray-300 ww-bg-white ww-text-gray-700 hover:ww-bg-gray-50 disabled:ww-opacity-50 disabled:ww-cursor-not-allowed"
        >
          Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={!agreedToTerms || loading}
          className={`ww-flex-1 ww-py-3 ww-rounded-lg ww-font-medium ww-text-sm ww-transition-colors ${
            agreedToTerms && !loading
              ? 'ww-bg-primary ww-text-white hover:ww-bg-primary/90'
              : 'ww-bg-gray-100 ww-text-gray-400 ww-cursor-not-allowed'
          }`}
        >
          {loading ? (
            <span className="ww-flex ww-items-center ww-justify-center ww-gap-2">
              <svg className="ww-animate-spin ww-w-4 ww-h-4" fill="none" viewBox="0 0 24 24">
                <circle className="ww-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="ww-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Confirming...
            </span>
          ) : (
            'Confirm appointment'
          )}
        </button>
      </div>
    </div>
  );
};

export default ConfirmationPanel;
